/**
 * User Goals Types
 *
 * Defines goals assigned to individual staff members from programs.
 * Enables tracking of personal performance targets.
 */

/**
 * Goal assigned to a user
 */
export interface UserGoal {
  id: number;
  user_id: number;
  user_name?: string;
  organization_id: number;
  program_id?: number;
  program_name?: string;
  goal_type: UserGoalType;
  title: string;
  description?: string;
  target_value: number;
  current_value: number;
  progress_percentage: number;
  unit: string;
  period_type: GoalPeriodType;
  period_start: string;
  period_end: string;
  status: UserGoalStatus;
  priority: GoalPriority;
  assigned_by: number;
  assigned_by_name?: string;
  notes?: string;
  created_at: string;
  updated_at?: string;
  completed_at?: string;
}

/**
 * Types of goals
 */
export type UserGoalType =
  | 'enrollment'        // Enroll X members
  | 'outreach'          // Complete X outreach activities
  | 'completion'        // Complete X programs/workflows
  | 'compliance'        // Maintain X% compliance
  | 'retention'         // Retain X% of members
  | 'documentation'     // Complete X documentation tasks
  | 'communication'     // Send X communications
  | 'visit'             // Complete X visits (home, office, etc.)
  | 'training'          // Complete X training modules
  | 'custom';           // Custom goal

/**
 * Goal period types
 */
export type GoalPeriodType =
  | 'daily'
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'quarterly'
  | 'annual'
  | 'custom';

/**
 * Goal status
 */
export type UserGoalStatus =
  | 'not_started'
  | 'in_progress'
  | 'on_track'
  | 'at_risk'
  | 'behind'
  | 'completed'
  | 'exceeded'
  | 'paused'
  | 'cancelled';

/**
 * Goal priority
 */
export type GoalPriority = 'low' | 'medium' | 'high' | 'critical';

/**
 * Create/Update payload for user goal
 */
export interface UserGoalCreate {
  user_id: number;
  program_id?: number;
  goal_type: UserGoalType;
  title: string;
  description?: string;
  target_value: number;
  unit: string;
  period_type: GoalPeriodType;
  period_start?: string;
  period_end?: string;
  priority?: GoalPriority;
  notes?: string;
}

/**
 * Update goal progress
 */
export interface GoalProgressUpdate {
  current_value: number;
  notes?: string;
}

/**
 * Bulk goal assignment (assign same goal to multiple users)
 */
export interface BulkGoalAssignment {
  user_ids: number[];
  program_id?: number;
  goal_type: UserGoalType;
  title: string;
  description?: string;
  target_value: number;
  unit: string;
  period_type: GoalPeriodType;
  period_start?: string;
  period_end?: string;
  priority?: GoalPriority;
}

/**
 * Goal template (reusable goal definition)
 */
export interface GoalTemplate {
  id: number;
  organization_id: number;
  program_id?: number;
  name: string;
  goal_type: UserGoalType;
  title_template: string;
  description_template?: string;
  default_target_value: number;
  unit: string;
  default_period_type: GoalPeriodType;
  default_priority: GoalPriority;
  is_active: boolean;
  created_at: string;
}

/**
 * Goal summary for dashboard
 */
export interface UserGoalSummary {
  user_id: number;
  total_goals: number;
  completed_goals: number;
  on_track_goals: number;
  at_risk_goals: number;
  behind_goals: number;
  overall_progress: number;
  goals_by_type: Record<UserGoalType, number>;
  goals_by_status: Record<UserGoalStatus, number>;
}

/**
 * Team goal summary (for supervisor view)
 */
export interface TeamGoalSummary {
  supervisor_id: number;
  team_size: number;
  total_team_goals: number;
  team_completion_rate: number;
  top_performers: Array<{
    user_id: number;
    user_name: string;
    completion_rate: number;
    goals_exceeded: number;
  }>;
  at_risk_members: Array<{
    user_id: number;
    user_name: string;
    behind_goals: number;
    at_risk_goals: number;
  }>;
  goals_by_type: Record<UserGoalType, {
    total: number;
    completed: number;
    avg_progress: number;
  }>;
}

/**
 * Goal type labels
 */
export const GOAL_TYPE_LABELS: Record<UserGoalType, { en: string; es: string; icon: string }> = {
  enrollment: { en: 'Enrollment', es: 'Inscripción', icon: 'UserPlus' },
  outreach: { en: 'Outreach', es: 'Alcance', icon: 'Users' },
  completion: { en: 'Completion', es: 'Finalización', icon: 'CheckCircle' },
  compliance: { en: 'Compliance', es: 'Cumplimiento', icon: 'Shield' },
  retention: { en: 'Retention', es: 'Retención', icon: 'Heart' },
  documentation: { en: 'Documentation', es: 'Documentación', icon: 'FileText' },
  communication: { en: 'Communication', es: 'Comunicación', icon: 'MessageSquare' },
  visit: { en: 'Visit', es: 'Visita', icon: 'MapPin' },
  training: { en: 'Training', es: 'Capacitación', icon: 'GraduationCap' },
  custom: { en: 'Custom', es: 'Personalizado', icon: 'Target' },
};

/**
 * Goal status labels with colors
 */
export const GOAL_STATUS_LABELS: Record<UserGoalStatus, { en: string; es: string; color: string }> = {
  not_started: { en: 'Not Started', es: 'No Iniciado', color: 'gray' },
  in_progress: { en: 'In Progress', es: 'En Progreso', color: 'blue' },
  on_track: { en: 'On Track', es: 'En Camino', color: 'green' },
  at_risk: { en: 'At Risk', es: 'En Riesgo', color: 'yellow' },
  behind: { en: 'Behind', es: 'Atrasado', color: 'red' },
  completed: { en: 'Completed', es: 'Completado', color: 'green' },
  exceeded: { en: 'Exceeded', es: 'Excedido', color: 'purple' },
  paused: { en: 'Paused', es: 'Pausado', color: 'gray' },
  cancelled: { en: 'Cancelled', es: 'Cancelado', color: 'gray' },
};

/**
 * Period type labels
 */
export const PERIOD_TYPE_LABELS: Record<GoalPeriodType, { en: string; es: string }> = {
  daily: { en: 'Daily', es: 'Diario' },
  weekly: { en: 'Weekly', es: 'Semanal' },
  biweekly: { en: 'Bi-weekly', es: 'Quincenal' },
  monthly: { en: 'Monthly', es: 'Mensual' },
  quarterly: { en: 'Quarterly', es: 'Trimestral' },
  annual: { en: 'Annual', es: 'Anual' },
  custom: { en: 'Custom Period', es: 'Período Personalizado' },
};

/**
 * Priority labels
 */
export const PRIORITY_LABELS: Record<GoalPriority, { en: string; es: string; color: string }> = {
  low: { en: 'Low', es: 'Bajo', color: 'gray' },
  medium: { en: 'Medium', es: 'Medio', color: 'blue' },
  high: { en: 'High', es: 'Alto', color: 'orange' },
  critical: { en: 'Critical', es: 'Crítico', color: 'red' },
};

/**
 * Calculate goal status based on progress and time remaining
 */
export function calculateGoalStatus(
  currentValue: number,
  targetValue: number,
  periodStart: string,
  periodEnd: string
): UserGoalStatus {
  const progress = (currentValue / targetValue) * 100;
  const now = new Date();
  const start = new Date(periodStart);
  const end = new Date(periodEnd);
  const totalDuration = end.getTime() - start.getTime();
  const elapsed = now.getTime() - start.getTime();
  const timeProgress = (elapsed / totalDuration) * 100;

  if (progress >= 100) {
    return progress > 100 ? 'exceeded' : 'completed';
  }

  if (now < start) {
    return 'not_started';
  }

  if (now > end) {
    return progress >= 100 ? 'completed' : 'behind';
  }

  // Compare progress to time elapsed
  const expectedProgress = timeProgress;
  const progressDiff = progress - expectedProgress;

  if (progressDiff >= 10) {
    return 'on_track';
  } else if (progressDiff >= -10) {
    return 'in_progress';
  } else if (progressDiff >= -25) {
    return 'at_risk';
  } else {
    return 'behind';
  }
}
